/**
 * PioneersTab — Admin Console tab for the iStudyPro mobile-waitlist
 * soft-launch (iter191). Lets admins:
 *
 *   • see how many people have joined the mobile waitlist
 *   • mint Pioneer claim invites for the oldest N pending signups
 *   • track who's been invited vs claimed
 *
 * Note: actual email delivery is fire-and-forget through Resend. Until
 * istudypro.ai is verified on Resend the emails are silently swallowed,
 * but the claim tokens are persisted so we can replay the batch later
 * (or share magic links manually from this table).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Crown, Send, Check, Mail, Users2, RefreshCw, Sunrise } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatTile = ({ label, value, color = 'text-[var(--f500-ink-1)]', testid }) => (
  <div className="rounded-2xl border border-[var(--f500-line)] bg-white/60 backdrop-blur p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-[var(--f500-ink-3)] font-semibold">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value ?? '—'}</div>
  </div>
);

const PioneersTab = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(100);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mobile-waitlist/admin/list`);
      setStats(data?.stats || null);
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendBatch = async () => {
    const n = Math.max(1, Math.min(500, Number(count) || 100));
    if (!window.confirm(`Mint Pioneer claim invites for the oldest ${n} pending signups? They each receive lifetime Pro on claim.`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/mobile-waitlist/pioneer-batch`, { count: n });
      toast.success(`${data.invited_count} invites minted (${data.upgraded_existing_accounts} accounts auto-upgraded).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  const [digestSending, setDigestSending] = useState(false);
  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const { data } = await axios.post(`${API}/admin-digest/test`);
      if (data.ok && data.sent > 0) {
        toast.success(`Digest sent to ${data.sent}/${data.recipients} admin(s).`);
      } else if (data.recipients === 0) {
        toast.warning('No admin recipients resolved. Set ADMIN_DIGEST_RECIPIENTS or add an is_admin user.');
      } else {
        toast.error(`Resend rejected all ${data.recipients} attempts. Check RESEND_API_KEY and domain status.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send digest');
    } finally {
      setDigestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[var(--f500-ink-3)]" data-testid="pioneers-tab-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading waitlist…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pioneers-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Signups" value={stats?.total} testid="pioneer-stat-total" />
        <StatTile label="Invited" value={stats?.invited} color="text-[#002FA7]" testid="pioneer-stat-invited" />
        <StatTile label="Claimed" value={stats?.claimed} color="text-emerald-600" testid="pioneer-stat-claimed" />
        <StatTile label="Pending" value={stats?.pending} color="text-amber-600" testid="pioneer-stat-pending" />
      </div>

      <div className="rounded-2xl border border-[var(--f500-line)] bg-gradient-to-r from-[#FFF8E1] to-white p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#F59E0B] flex items-center justify-center flex-shrink-0">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-base text-[var(--f500-ink-1)]">Mint Pioneer invites</h3>
            <p className="text-xs text-[var(--f500-ink-3)] mt-0.5 max-w-md">
              Picks the oldest pending waitlist signups, mints a single-use claim token for each, and dispatches the invite email via Resend. Existing accounts get upgraded to Pro immediately.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            type="number"
            min={1}
            max={500}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="w-20 px-3 py-2 rounded-lg border border-[var(--f500-line)] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
            data-testid="pioneer-batch-count"
          />
          <Button
            onClick={sendBatch}
            disabled={sending || !stats?.pending}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white gap-2"
            data-testid="pioneer-send-batch-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send invites
          </Button>
          <Button
            onClick={load}
            variant="outline"
            className="gap-2"
            data-testid="pioneer-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* iter191c — daily ops digest trigger */}
      <div className="rounded-2xl border border-[var(--f500-line)] bg-gradient-to-r from-[#EFF6FF] to-white p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between" data-testid="admin-digest-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#4F2BB8] flex items-center justify-center flex-shrink-0">
            <Sunrise className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-base text-[var(--f500-ink-1)]">Daily Ops Digest</h3>
            <p className="text-xs text-[var(--f500-ink-3)] mt-0.5 max-w-md">
              Auto-emailed every morning at 06:00 UTC to every admin (signups, waitlist, Pioneer claim funnel, support queue, revenue). Press to send a sample now.
            </p>
          </div>
        </div>
        <Button
          onClick={sendDigest}
          disabled={digestSending}
          variant="outline"
          className="gap-2 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/10"
          data-testid="send-admin-digest-btn"
        >
          {digestSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Send sample now
        </Button>
      </div>

      <div className="rounded-2xl border border-[var(--f500-line)] bg-white overflow-hidden" data-testid="pioneer-list">
        <div className="px-4 py-3 border-b border-[var(--f500-line)] flex items-center gap-2 text-sm font-semibold text-[var(--f500-ink-1)]">
          <Users2 className="w-4 h-4 text-[#002FA7]" /> Waitlist (oldest first)
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--f500-ink-3)]">No waitlist signups yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--f500-surface-2)] text-[11px] uppercase tracking-wide text-[var(--f500-ink-3)]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Email</th>
                  <th className="text-left px-4 py-2 font-semibold hidden sm:table-cell">Joined</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Source</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.email} className="border-t border-[var(--f500-line)] hover:bg-[var(--f500-surface-2)]/40">
                    <td className="px-4 py-2 font-medium text-[var(--f500-ink-1)] truncate max-w-[220px]">{r.email}</td>
                    <td className="px-4 py-2 text-xs text-[var(--f500-ink-3)] hidden sm:table-cell">{(r.joined_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs text-[var(--f500-ink-3)] hidden md:table-cell">{r.source || '—'}</td>
                    <td className="px-4 py-2">
                      {r.pioneer_claimed_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><Check className="w-3 h-3" /> Claimed</span>
                      ) : r.pioneer_invited_at ? (
                        <span className="inline-flex items-center gap-1 text-[#002FA7] text-xs font-semibold"><Mail className="w-3 h-3" /> Invited</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PioneersTab;
